import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: translations.nav.about[language], path: '/about' },
    { name: translations.nav.services[language], path: '/services' },
    { name: translations.nav.news[language], path: '/news' },
    { name: translations.nav.careers[language], path: '/careers' },
    { name: translations.nav.contact[language], path: '/contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-primary shadow-lg' : 'bg-primary/95 backdrop-blur-sm'
      }`}
      data-testid="header"
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo and Company Name */}
          <Link
            to="/"
            className="flex items-center gap-2 md:gap-3 hover:opacity-90 transition-opacity min-w-0 flex-1"
            data-testid="logo-link"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_chengyang-taipei/artifacts/xw286hjg_chenyangcpalogo.png"
              alt="誠揚聯合會計師事務所 Logo"
              className="h-8 md:h-12 w-auto object-contain flex-shrink-0"
              style={{ mixBlendMode: 'lighten' }}
            />
            {language === 'en' ? (
              <span className="text-white font-noto-sans font-medium text-[13px] md:text-base lg:text-lg leading-tight">
                <span className="block md:inline">Cheng Yang &amp; Co., CPAs</span>
                <span className="block md:inline md:before:content-['_']">Taipei Division</span>
              </span>
            ) : (
              <span className="text-white font-noto-sans font-medium text-[13px] md:text-base lg:text-lg truncate leading-tight">
                {translations.header.companyName[language]}
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 flex-shrink-0" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-white/90 hover:text-secondary transition-colors font-noto-sans text-sm font-medium ${
                  location.pathname === item.path ? 'text-secondary' : ''
                }`}
                data-testid={`nav-link-${item.path.slice(1)}`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Language Toggle Button */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/30 hover:border-secondary hover:bg-secondary/10 transition-all text-white/90 hover:text-secondary"
              data-testid="language-toggle"
              aria-label="Toggle language"
            >
              <Globe size={16} />
              <span className="text-sm font-medium">{language === 'zh' ? 'EN' : '中'}</span>
            </button>
          </nav>

          {/* Mobile Menu Button & Language Toggle */}
          <div className="lg:hidden flex items-center gap-2 flex-shrink-0 ml-3">
            <button
              onClick={toggleLanguage}
              className="flex items-center justify-center w-8 h-8 rounded-full border border-white/30 text-white/90 hover:border-secondary hover:text-secondary transition-colors"
              data-testid="mobile-language-toggle"
              aria-label="Toggle language"
            >
              <span className="text-xs font-medium">{language === 'zh' ? 'EN' : '中'}</span>
            </button>
            <button
              className="text-white p-2 -mr-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden pb-4 animate-fade-in" data-testid="mobile-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block py-3 text-white/90 hover:text-secondary transition-colors font-noto-sans ${
                  location.pathname === item.path ? 'text-secondary' : ''
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${item.path.slice(1)}`}
              >
                {item.name}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
